import { $fetch } from "ofetch";
import { defineStore } from "pinia";
import type Organisation from "@backend/organisation";
import type User from "@backend/user";
import type { GetUsersResponse } from "~/types/global";
import type FamilyGroup from "@backend/family_group";

export const useAuthStore = defineStore("auth", () => {
  const { $api } = useNuxtApp();
  const familyGroupsStore = useGroupStore();
  const transmitStore = useTransmitStore();
  const lockModal = useState<boolean>("auth.lockModal", () => false);

  // User data Global States
  const permissions = ref<Record<string, string[]>>({});
  const user: Ref<User | null> = useState("auth.user", () => null);
  const orga: Ref<Partial<Organisation> | null> = useState("auth.orga", () => null);
  // const sidebarCollapsed: Ref<boolean> = useState("auth.sidebarCollapsed", () => false);

  // Use a more robust approach for SSR-friendly sidebar state
  const sidebarCollapsedCookie = useCookie("dash-sidebar", {
    default: () => false,
    sameSite: "lax",
    secure: false,
  });

  // Create a ref that syncs with the cookie
  const sidebarCollapsed = ref(sidebarCollapsedCookie.value);

  // Watch for changes and sync with cookie
  watch(
    sidebarCollapsed,
    (newValue) => {
      sidebarCollapsedCookie.value = newValue;
    },
    { immediate: false },
  );

  // Watch for cookie changes and sync with ref
  watch(
    sidebarCollapsedCookie,
    (newValue) => {
      sidebarCollapsed.value = newValue;
    },
    { immediate: false },
  );
  const currentOrganisation: Ref<number | null> = ref(null);

  const isConnected: Ref<boolean> = useState("auth.isConnected", () => false);
  const isLoggingOut: Ref<boolean> = useState("auth.isLoggingOut", () => false);

  // IS THIS REALLY USEFULL ????
  // Handle SSR/client state synchronization
  if (import.meta.client) {
    // On client, ensure we can modify the state
    watchEffect(() => {
      if (user.value && !isConnected.value) {
        isConnected.value = true;
      }
    });
  }

  const isAuthenticated = async () => {
    try {
      // If we're already in the process of logging out, don't check
      if (isLoggingOut.value) {
        return false;
      }

      // Force check on server-side, use cache on client-side unless user is null
      const forceCheck = import.meta.server || !user.value;
      // console.log('[auth.ts isAuthenticated] forceCheck:', forceCheck)
      // console.log('[auth.ts isAuthenticated] Running on:', import.meta.server ? 'server' : 'client')
      // console.log('[auth.ts isAuthenticated] Calling me()...')

      const userData = await me(forceCheck);

      // Safely update isConnected
      try {
        isConnected.value = !!userData;
        // console.log('[auth.ts isAuthenticated] isConnected set to:', isConnected.value)
      } catch (error) {
        console.warn("[auth.ts isAuthenticated] Error setting isConnected:", error);
      }
      return !!userData;
    } catch (e) {
      console.error("[auth.ts isAuthenticated] Error:", e);
      try {
        isConnected.value = false;
        user.value = null; // Clear user data on error
      } catch (error) {
        console.warn("[auth.ts isAuthenticated] Error updating state:", error);
      }
      return false;
    }
  };

  async function api<ResponseType = unknown>(
    method: string,
    url: string,
    bodyPayload: Record<string, unknown> | FormData = {},
    customheaders = {},
    options: { signal?: AbortSignal; stream?: boolean } = {},
  ): Promise<ResponseType | Response> {
    const rawBodyPayload = toRaw(unref(bodyPayload));

    const queryString =
      rawBodyPayload instanceof FormData
        ? ""
        : rawBodyPayload.queryString
          ? "?" +
            new URLSearchParams(rawBodyPayload.queryString as Record<string, string>).toString()
          : "";
    if (!(rawBodyPayload instanceof FormData)) {
      // remove queryString from bodyPayload
      delete rawBodyPayload.queryString;
    }

    const finalUrl = `${$api(url)}${queryString}`;

    const headers: HeadersInit = { ...customheaders };
    if (import.meta.server) {
      const requestHeaders = useRequestHeaders(["cookie"]);
      if (requestHeaders.cookie) {
        headers.cookie = requestHeaders.cookie;
      }
    }

    let finalBody: BodyInit | undefined;
    const methodUpper = method.toUpperCase();

    switch (methodUpper) {
      case "GET":
      case "HEAD":
        finalBody = undefined; // Crucially, undefined for GET/HEAD
        break;
      default: // Covers POST, PUT, DELETE, PATCH, etc.
        if (rawBodyPayload instanceof FormData) {
          finalBody = rawBodyPayload;
          // Let the browser set the Content-Type header with the correct boundary
          delete headers["Content-Type"];
        } else if (rawBodyPayload && Object.keys(rawBodyPayload).length > 0) {
          finalBody = JSON.stringify(rawBodyPayload);
        } else {
          finalBody = undefined; // Body is intentionally empty
        }
        break;
    }

    // Streaming mode returns the raw fetch Response so the caller can consume NDJSON chunks.
    if (options.stream) {
      return fetch(finalUrl, {
        method: methodUpper,
        credentials: "include",
        headers,
        body: finalBody,
        signal: options.signal,
      });
    }

    const response = await $fetch(finalUrl, {
      method: methodUpper, // Use consistent methodUpper
      credentials: "include",
      headers: headers,
      body: finalBody,
      ignoreResponseError: false,
    }).catch((error) => {
      throw normalizeFetchError(error);
    });

    return response;
  }

  async function authenticate(_result: unknown) {
    isConnected.value = true;
    await me(true); // Call me() to populate user object with force=true
  }

  async function addContact(email: string | undefined) {
    try {
      const res = await fetch($api("/contact/new"), {
        method: "POST",
        body: JSON.stringify({ email }),
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Backend Error:", errorData); // Debug
        throw new Error(errorData.message); // 🚨 Ensure it propagates
      }

      return await res.json();
    } catch (error) {
      console.error("Network or API Error:", error.message); // Debug
      throw error; // 🚨 Throw error so `onSubmit` can catch it
    }
  }

  async function login(payload: Record<string, unknown> | undefined, remember: boolean) {
    try {
      const result = (await api("post", "/login", payload)) as unknown as {
        message: string;
        remember: boolean;
      };
      result.remember = remember;
      await authenticate(result);
      isConnected.value = true;
      await me(true); // Call me() to populate user object with force=true
      return result.message;
    } catch (error) {
      console.warn("login error", error);
      throw error;
    }
  }

  async function loginOtp() {
    try {
      await authenticate({ message: "Login successful" });
      isConnected.value = true;
      await me(true); // Call me() to populate user object with force=true
      return "Login successful";
    } catch (error) {
      console.error("Login Error:", error);

      // Throw the actual error message from the API
      if (error instanceof Error) {
        throw error; // Re-throw the error to handle it in the component
      } else {
        // throw new Error('Login failed. Please try again.'); // Fallback error message
      }
    }
  }

  async function loginGoogle() {
    try {
      // console.log("check loginGoogle");
      const result = (await api("post", "/auth/google/login", {})) as unknown as {
        message: string;
      };

      if (result.message === "Google OAuth successful") {
        await authenticate(result);
        isConnected.value = true;
        // Force refresh user data to ensure we have the latest state
        await me(true);
        return result.message;
      } else {
        throw new Error("Google OAuth authentication failed");
      }
    } catch (error) {
      console.error("Google Login Error:", error);

      // Clear any partial authentication state
      isConnected.value = false;
      user.value = null;

      // Throw the actual error message from the API
      if (error instanceof Error) {
        throw error; // Re-throw the error to handle it in the component
      } else {
        throw new Error("Google OAuth failed. Please try again.");
      }
    }
  }

  async function register(payload: unknown | undefined) {
    try {
      const result = (await api(
        "post",
        `/auth/user/register`,
        payload as Record<string, unknown>,
      )) as unknown as {
        message: string;
      };
      authenticate(result);
      return result;
    } catch (error) {
      console.error("Register Error:", error);
      throw error;
    }
  }

  async function setup(payload: Record<string, unknown> | undefined) {
    let result = null;
    try {
      result = (await api("post", `/auth/user/setup/`, payload)) as unknown as {
        message: string;
      };
      await me(true);
    } catch (error) {
      return error;
    }
    return result;
  }

  async function logout() {
    try {
      isLoggingOut.value = true;

      // Navigate FIRST - this starts unmounting the current page
      await navigateTo("/", { replace: true });

      // Wait for Vue to fully complete the navigation cycle and unmount old components
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now safely do all cleanup - old page watchers are destroyed
      await transmitStore.cleanup();

      isConnected.value = false;
      user.value = null;
      orga.value = null;
      permissions.value = {};
      currentOrganisation.value = null;
      familyGroupsStore.setGroups([], true);

      // Call the logout API to invalidate the session on the server
      try {
        await $fetch($api("/logout"), {
          method: "DELETE",
          credentials: "include",
        });
      } catch (error) {
        console.warn("Logout API call failed:", error);
      }

      if (import.meta.server) {
        clearNuxtData("auth.user");
        clearNuxtData("auth.isConnected");
      }
    } catch (error) {
      console.error("Logout error:", error);

      // Still clear state even on error
      await transmitStore.cleanup();
      isConnected.value = false;
      user.value = null;
      orga.value = null;
      permissions.value = {};
      currentOrganisation.value = null;
      familyGroupsStore.setGroups([]);

      try {
        await navigateTo("/", { replace: true });
      } catch {
        // Ignore navigation errors
      }
    } finally {
      isLoggingOut.value = false;
    }
  }

  async function changePassword(payload: unknown | undefined) {
    try {
      const data = await api("post", `/password/update`, payload as Record<string, unknown>);
      return data;
    } catch (error) {
      throw error; // Re-throw the error to handle it in the component
    }
  }

  async function me(force: boolean = false) {
    if (!user.value || force) {
      try {
        // Define the expected response type for /me endpoint
        type MeResponseType = {
          user: User;
          permissions: Record<string, string[]>;
          orga: Partial<Organisation>;
          groups: FamilyGroup[];
        };

        // Make the API call
        const result = await api<MeResponseType>("get", "/me"); // No .value needed

        if (result?.user) {
          user.value = result.user;
          permissions.value = result.permissions;
          orga.value = result.orga;
          // Add null check for orga.value before accessing id
          currentOrganisation.value = result.orga?.id ?? null;

          if (user.value.organisation_id) {
            familyGroupsStore.setGroups(result.groups);
          }
          // Set user's available groups and handle auto-selection
          if (result.user.family_groups) {
            familyGroupsStore.setUserGroups(result.user.family_groups);
          }
          return user.value as User; // Return the user if everything is fine
        } else {
          // Handle the case where result or result.user is null
          return null;
        }
      } catch (error) {
        console.error("[auth.ts me] Error fetching user data:", error);
        return null;
      }
    } else {
      // If user.value exists and force is false, return the existing user
      return user.value as User;
    }
  }

  async function getUserCount() {
    try {
      if (user.value && permissions.value.permissions?.includes("index")) {
        const data = await api<{ count: number }>("get", `/orga/usercount/`);
        // Access the value from the ref before accessing its properties
        if (data) {
          // Check if data itself is not null/undefined
          return data.count;
        }
        return 0; // Or handle as an error/undefined
      }
    } catch (error) {
      console.error("Get User Count Error:", error);
      return 0;
    }
  }

  async function getUsers() {
    if (user.value && permissions.value.orga?.includes("list_users")) {
      try {
        const data = await api("get", `/orga/getusers/`);
        return data as GetUsersResponse;
      } catch (error) {
        console.error("Get Users Error:", error);
        return [];
      }
    }
    return [];
  }

  async function getPermissions() {
    if (user.value && permissions.value.permissions?.includes("index")) {
      try {
        const data = await api("get", `/orga/permissions/`);
        return data;
      } catch (error) {
        console.error("Get Permissions Error:", error);
        return null;
      }
    }
    return null;
  }

  async function submitFeedback(feedbackData: {
    message: string;
    pageSource: string;
    userAgent: string;
    timestamp: string;
  }) {
    try {
      const result = await api("post", "/feedback/submit", feedbackData);
      return result;
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  }

  async function deleteAccount(password: string) {
    try {
      const result = await api("delete", "/profile/delete", { password });
      // Clear auth state after successful deletion
      isConnected.value = false;
      user.value = null;
      orga.value = null;
      permissions.value = {};
      currentOrganisation.value = null;
      familyGroupsStore.setGroups([]);
      await logout();
      return result;
    } catch (error) {
      console.error("Error deleting account:", error);
      throw error;
    }
  }

  const userStats = ref<{
    tasks: number;
    observations: number;
    direct_works: number;
  } | null>(null);

  async function getUserStats() {
    try {
      const response = await api<{
        data: {
          tasks: number;
          observations: number;
          direct_works: number;
        };
      }>("get", "/auth/stats");
      userStats.value = response.data;
      return response.data;
    } catch (error) {
      console.error("Error getting user stats:", error);
      return {
        tasks: 0,
        observations: 0,
        direct_works: 0,
      };
    }
  }

  return {
    // status info states
    user,
    userStats,
    permissions,
    orga,
    isConnected,
    isLoggingOut,
    currentOrganisation,
    sidebarCollapsed,

    // Methods for backend
    isAuthenticated,
    addContact,
    login,
    loginOtp,
    loginGoogle,
    register,
    setup,
    logout,
    me,
    getUserCount,
    getUsers,
    getPermissions,
    getUserStats,
    submitFeedback,
    deleteAccount,
    api,
    authenticate,
    changePassword,

    // display properties for Editor
    lockModal,
  };
});
